import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import Header from '../../components/layout/Header';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
  ChevronRight, Search, Users, UserCog, UserCheck, User, Phone, Mail, Crown, Shield,
} from 'lucide-react';
import './Team.css';

// ── Role-based icon + accent ─────────────────────────────────
const ROLE_META = {
  master:        { Icon: Crown,     accent: '#e6197a', label: 'Master' },
  admin:         { Icon: Shield,    accent: '#1d4ed8', label: 'Admin' },
  team_leader:   { Icon: Users,     accent: '#8b5cf6', label: 'Team Leader' },
  sales_manager: { Icon: UserCog,   accent: '#f59e0b', label: 'Sales Manager' },
  tele_caller:   { Icon: UserCheck, accent: '#14b8a6', label: 'Tele Caller' },
};
const getRoleMeta = (slug) => ROLE_META[slug] || { Icon: User, accent: '#6b7280', label: slug || 'User' };

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Count descendants (excluding the node itself), grouped by role
const countDescendants = (node) => {
  let salesManagers = 0;
  let teleCallers = 0;
  const walk = (n) => {
    n.children.forEach(c => {
      if (c.role_slug === 'sales_manager') salesManagers += 1;
      if (c.role_slug === 'tele_caller') teleCallers += 1;
      walk(c);
    });
  };
  walk(node);
  return { salesManagers, teleCallers, direct: node.children.length };
};

// ── Tree node ────────────────────────────────────────────────
const TreeNode = ({ node, depth, query }) => {
  const [open, setOpen] = useState(true);
  const meta = getRoleMeta(node.role_slug);
  const RoleIcon = meta.Icon;
  const hasChildren = node.children && node.children.length > 0;
  const counts = useMemo(() => countDescendants(node), [node]);

  const matchesQuery = !query || (
    (node.name || '').toLowerCase().includes(query) ||
    (node.username || '').toLowerCase().includes(query) ||
    (node.email || '').toLowerCase().includes(query) ||
    (meta.label || '').toLowerCase().includes(query)
  );

  // Drop entire branch only if this node and ALL descendants don't match
  const branchHasMatch = useMemo(() => {
    if (matchesQuery) return true;
    const walk = (n) => {
      if ((n.name || '').toLowerCase().includes(query) ||
          (n.username || '').toLowerCase().includes(query) ||
          (n.email || '').toLowerCase().includes(query)) return true;
      return n.children.some(walk);
    };
    return query ? walk(node) : true;
  }, [node, query, matchesQuery]);

  if (!branchHasMatch) return null;

  return (
    <div className={`th-node th-node--depth-${depth}`}>
      <div
        className={`th-row${matchesQuery && query ? ' th-row--match' : ''}`}
        style={{ '--th-accent': meta.accent }}
      >
        <button
          className={`th-toggle${open ? ' th-toggle--open' : ''}`}
          onClick={() => hasChildren && setOpen(o => !o)}
          disabled={!hasChildren}
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          {hasChildren ? <ChevronRight size={14} /> : <span className="th-toggle__dot" />}
        </button>

        <div className="th-avatar" style={{ borderColor: meta.accent, color: meta.accent }}>
          {getInitials(node.name)}
        </div>

        <div className="th-main">
          <div className="th-main__top">
            <span className="th-name">{node.name}</span>
            <span className="th-role-badge" style={{ background: meta.accent + '14', color: meta.accent }}>
              <RoleIcon size={11} /> {meta.label}
            </span>
            {!node.is_active && <span className="th-inactive">Inactive</span>}
          </div>
          <div className="th-main__bottom">
            {node.username && <span className="th-meta">@{node.username}</span>}
            {node.mobile && <span className="th-meta"><Phone size={11} /> {node.mobile}</span>}
            {node.email && <span className="th-meta"><Mail size={11} /> {node.email}</span>}
          </div>
        </div>

        {hasChildren && (
          <div className="th-counts">
            {counts.salesManagers > 0 && (
              <span className="th-count-pill" title="Sales Managers in branch">
                <UserCog size={11} /> {counts.salesManagers}
              </span>
            )}
            {counts.teleCallers > 0 && (
              <span className="th-count-pill" title="Tele Callers in branch">
                <UserCheck size={11} /> {counts.teleCallers}
              </span>
            )}
          </div>
        )}
      </div>

      {hasChildren && open && (
        <div className="th-children">
          {node.children.map(c => (
            <TreeNode key={c.u_id} node={c} depth={depth + 1} query={query} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────
const Team = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('users/hierarchy');
      if (res.success) {
        setTree(res.data?.tree || []);
      } else {
        showToast(res.message || 'Failed to load team', 'error');
      }
    } catch (err) {
      console.error('Hierarchy load error:', err);
      showToast('Failed to load team', 'error');
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    let teamLeaders = 0, salesManagers = 0, teleCallers = 0, total = 0;
    const walk = (n) => {
      total += 1;
      if (n.role_slug === 'team_leader') teamLeaders += 1;
      else if (n.role_slug === 'sales_manager') salesManagers += 1;
      else if (n.role_slug === 'tele_caller') teleCallers += 1;
      n.children.forEach(walk);
    };
    tree.forEach(walk);
    return { teamLeaders, salesManagers, teleCallers, total };
  }, [tree]);

  const subtitle = (() => {
    const slug = user?.role_slug;
    if (slug === 'master' || slug === 'admin') return 'Full organization hierarchy';
    if (slug === 'team_leader') return 'Your sales managers and tele callers';
    if (slug === 'sales_manager') return 'Your tele callers';
    return 'Your team';
  })();

  const q = search.trim().toLowerCase();

  return (
    <div>
      <Header title="Team Hierarchy" subtitle={subtitle} />
      <div className="page">
        <div className="th-stats">
          <div className="th-stat"><span className="th-stat__val">{stats.total}</span><span className="th-stat__lbl">Total</span></div>
          <div className="th-stat"><span className="th-stat__val">{stats.teamLeaders}</span><span className="th-stat__lbl">Team Leaders</span></div>
          <div className="th-stat"><span className="th-stat__val">{stats.salesManagers}</span><span className="th-stat__lbl">Sales Managers</span></div>
          <div className="th-stat"><span className="th-stat__val">{stats.teleCallers}</span><span className="th-stat__lbl">Tele Callers</span></div>
        </div>

        <div className="th-search">
          <Search size={15} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, username, email, role..."
          />
        </div>

        <div className="th-tree">
          {loading ? (
            <div className="th-empty">Loading team hierarchy...</div>
          ) : tree.length === 0 ? (
            <div className="th-empty">No team members found.</div>
          ) : (
            tree.map(root => (
              <TreeNode key={root.u_id} node={root} depth={0} query={q} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Team;
